import React, { forwardRef, type ElementType, type HTMLAttributes } from 'react';
import { AssessmentCard, type AssessmentRank } from '../AssessmentCard';
import styles from './AssessmentExample.module.css';

/*
 * AssessmentExample — read-only reference display of the full rating scale.
 *
 * Shows all five rating options as a row of cards, with one card optionally
 * highlighted as a worked example. Unlike the interactive picker, nothing is
 * pickable here — the scale is shown so the reader understands every option
 * before they start answering.
 *
 * Two layout tiers:
 *   md (≥768): the five cards sit in a 5-up row (large display numbers above
 *     each title).
 *   sm (<768): the cards stack into a full-width list, each with the number
 *     beside its title and the description below.
 */

export interface AssessmentExampleItem {
  number: AssessmentRank;
  title: string;
  description: string;
}

export type AssessmentExampleLayout = 'sm' | 'md';

export interface AssessmentExampleProps extends HTMLAttributes<HTMLDivElement> {
  /** The five scale options rendered as cards, left → right (md) / top → bottom (sm). */
  items: readonly AssessmentExampleItem[];
  /**
   * Rank rendered with the highlighted visual as the worked example
   * (e.g. 3 to spotlight "Moderate experience"). Omit for no highlight.
   */
  example?: AssessmentRank;
  /**
   * Pin the layout tier (skips matchMedia auto-detect).
   *   - `sm` = stacked full-width list (viewport < 768)
   *   - `md` = 5-up card row (viewport ≥ 768)
   */
  forceLayout?: AssessmentExampleLayout;
  /**
   * Explicit width override. Without this the scale fills its column
   * (`100%`). Regression sidecars pin a specific source width so screenshot
   * baselines are deterministic.
   */
  width?: number | string;
  /**
   * Colour treatment forwarded to each AssessmentCard. `dark` (default) is the
   * frosted-glass card; `light` is the light-surface card. Per-instance variant.
   */
  theme?: 'light' | 'dark';
  /** Render the root as a different element/component. Default `'div'`. */
  as?: ElementType;
}

export const AssessmentExample = forwardRef<HTMLDivElement, AssessmentExampleProps>(
  function AssessmentExample(
    { items, example, forceLayout, width = '100%', theme = 'dark', as, className, style, ...rest },
    ref,
  ) {
  const Component = as ?? 'div';
  const [isBelow, setIsBelow] = React.useState<boolean>(forceLayout === 'sm');

  React.useEffect(() => {
    if (forceLayout) {
      setIsBelow(forceLayout === 'sm');
      return;
    }
    if (typeof window === 'undefined') return;
    // Matches the assessment screens' 768 breakpoint: the row collapses to a
    // stacked list below 768.
    const mq = window.matchMedia('(max-width: 767px)');
    const update = () => setIsBelow(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, [forceLayout]);

  const rootClass = [styles.example, className].filter(Boolean).join(' ');

  return (
    <Component
      ref={ref}
      className={rootClass}
      data-layout={isBelow ? 'sm' : 'md'}
      style={{ width, ...style }}
      {...rest}
    >
      {items.map((item) => (
        <AssessmentCard
          key={item.number}
          number={item.number}
          title={item.title}
          description={item.description}
          selected={example === item.number}
          orientation={isBelow ? 'inline' : 'stacked'}
          interactive={false}
          theme={theme}
        />
      ))}
    </Component>
  );
  },
);

AssessmentExample.displayName = 'AssessmentExample';
